import { calculateVatBreakdown, TaxTreatment } from '@longy/common'

export const formatPrice = (price: number): string =>
    new Intl.NumberFormat('hu-HU', {
        style: 'decimal',
        maximumFractionDigits: 0,
    }).format(price) + ' Ft'

/**
 * How one catalogue price reads, given the treatment it will be billed at — the same two states
 * the app's `PurchasePriceBlock` has, so a ticket reads the same in both places.
 *
 * A VAT-liable price is quoted net with the VAT named beside it, because that is how a ticket is
 * sold. Everything else — exempt, margin, no treatment at all — is quoted gross with nothing
 * written next to it: there is no VAT to add, and there is no second figure for a label to
 * distinguish it from.
 *
 * A zero price renders freeLabel with no amount and no suffix — there is no VAT on nothing, and
 * "0 Ft + ÁFA" reads like a free registration carries a hidden cost. Callers with no free label
 * keep the old behaviour.
 *
 * `netPlusVat` is a plain closure over the formatted amount rather than i18next's `t`. This module
 * stays pure and testable either way, but handing an overloaded `t` to a narrow function parameter
 * crashes tsc outright ("Debug Failure. No error for last overload signature") when the call sits
 * in a JSX attribute, which it does at both call sites.
 */
export const priceFormatterFor = (
    tax: TaxTreatment | null | undefined,
    netPlusVat: (price: string) => string,
    freeLabel?: string,
): ((price: number) => string) => {
    const format = (price: number) => {
        if (tax?.kind !== 'vat') {
            return formatPrice(price)
        }

        return netPlusVat(formatPrice(calculateVatBreakdown(price, tax).net))
    }

    return (price: number) => (freeLabel !== undefined && price === 0 ? freeLabel : format(price))
}
